import { useState, useEffect } from "react";
import { 
  PhoneMissed, Phone, RefreshCw, AlertTriangle, 
  ChevronDown, ChevronUp, Clock, User, X
} from "lucide-react";

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function AppelsMissedBanner({ selectedTelepro = "", defaultExpanded = false }) {
  const [missedData, setMissedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showDetails, setShowDetails] = useState(defaultExpanded);

  useEffect(() => {
    loadMissedCalls();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadMissedCalls, 30 * 1000);
    return () => clearInterval(interval);
  }, [selectedTelepro]);

  const loadMissedCalls = async () => {
    try {
      const url = selectedTelepro 
        ? `${API}/ringover/missed-calls?telepro=${encodeURIComponent(selectedTelepro)}&today_only=true`
        : `${API}/ringover/missed-calls?today_only=true`;
      
      const resp = await authFetch(url);
      if (resp.ok) {
        const data = await resp.json();
        setMissedData(data);
      }
    } catch (error) {
      console.error("Erreur chargement appels manqués:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCallBack = (phoneNumber) => {
    if (phoneNumber) {
      window.open(`tel:${phoneNumber.replace(/\s/g, '')}`, '_blank');
    }
  };

  // Pas d'appels manqués = pas de bannière
  if (!loading && (!missedData || missedData.total_missed === 0)) {
    return null;
  }

  const totalMissed = missedData?.total_missed || 0;
  const byTelepro = missedData?.stats_by_telepro || {};

  return (
    <div 
      data-testid="missed-calls-banner"
      className={`mb-4 rounded-xl border overflow-hidden transition-all ${
        totalMissed > 5 
          ? 'bg-red-900/30 border-red-500/50' 
          : totalMissed > 0 
            ? 'bg-orange-900/30 border-orange-500/50'
            : 'bg-slate-800/50 border-slate-700/50'
      }`}
    >
      {/* Header cliquable */}
      <div 
        className="p-4 cursor-pointer hover:bg-slate-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              totalMissed > 5 ? 'bg-red-500/30 animate-pulse' : 'bg-orange-500/30'
            }`}>
              <PhoneMissed className={`w-5 h-5 ${
                totalMissed > 5 ? 'text-red-400' : 'text-orange-400'
              }`} />
            </div>
            <div>
              <h3 className="font-bold text-white flex items-center gap-2">
                📞 Appels entrants manqués
                <span className={`px-2 py-0.5 text-sm font-bold rounded-full ${
                  totalMissed > 5 
                    ? 'bg-red-500 text-white' 
                    : 'bg-orange-500 text-white'
                }`}>
                  {totalMissed}
                </span>
              </h3>
              <p className="text-sm text-slate-400">
                Aujourd'hui • Dernière MAJ: {missedData?.current_time || '--:--'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); loadMissedCalls(); }}
              className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>

        {/* Résumé par télépro (toujours visible) */}
        {Object.keys(byTelepro).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {Object.entries(byTelepro).map(([telepro, data]) => (
              <span 
                key={telepro}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  data.count > 3 
                    ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
                    : 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                }`}
              >
                {telepro}: {data.count} manqué{data.count > 1 ? 's' : ''}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Détails des appels (expandable) */}
      {expanded && (
        <div className="border-t border-slate-700/50 p-4 max-h-[400px] overflow-y-auto">
          <div className="space-y-2">
            {Object.entries(byTelepro).map(([telepro, data]) => (
              <div key={telepro} className="bg-slate-800/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-white flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    {telepro}
                  </span>
                  <span className={`text-sm font-bold ${
                    data.count > 3 ? 'text-red-400' : 'text-orange-400'
                  }`}>
                    {data.count} appel{data.count > 1 ? 's' : ''} manqué{data.count > 1 ? 's' : ''}
                  </span>
                </div>
                
                {/* Liste des appels manqués */}
                <div className="space-y-1">
                  {data.calls.map((call, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between py-1 px-2 bg-slate-900/50 rounded text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-400">{call.heure || '--:--'}</span>
                        <span className="text-white">
                          {call.contact_name && call.contact_name !== 'Inconnu' 
                            ? call.contact_name 
                            : (call.from_number || call.from_number_masked || 'N° inconnu')}
                        </span>
                      </div>
                      <button
                        onClick={() => handleCallBack(call.from_number)}
                        className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-500 text-white rounded text-xs transition-colors"
                        title="Rappeler"
                      >
                        <Phone className="w-3 h-3" />
                        Rappeler
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Message d'alerte pour Fatima */}
          {totalMissed > 0 && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <strong>Rappel process :</strong> Ne jamais louper un appel entrant ! 
                Ces prospects doivent être rappelés immédiatement.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
